import React, { useCallback, useState } from 'react';
import cn from 'classnames';
import { FormattedMessage } from 'react-intl';
import AuInput, { INPUT_TYPE_CHECKBOX } from '@au/core/lib/components/elements/AuInput';

import fieldset from '../../../css/fieldset.module.scss';

/**
 * CheckboxesInput
 *
 * Example YAML usage:
 * ```yaml
 * attributes:
 *   allowedEnvs:
 *     rules: required
 *     display:
 *       create:
 *         component: CheckboxesInput
 *         props:
 *           defaultValues:
 *             - dev
 *             - staging
 *           options:
 *             - dev
 *             - staging
 * ```
 *
 * @param {string[]} [props.defaultValues=[]]
 *   The options that should be checked by default when the component
 *   first mounts. Configured via YAML `props.defaultValues`.
 * @param {string[]} [props.options=[]]
 *   The list of selectable values, rendered as one checkbox per entry
 *   (using the option's own string value as its `name`, `caption`, and
 *   `key`). Configured via YAML `props.options`.
 *
 * @returns {JSX.Element} A labeled group of checkboxes.
 */
export const CheckboxesInput = ({
  // generic props
  field,
  fieldChangeCallback,
  labelId,
  disabled,
  showErrors,
  validationErrors,
  // extra props specifically for this Input
  defaultValues = [],
  options = [],
}) => {
  const [values, setValues] = useState(defaultValues);

  const error = validationErrors && field in validationErrors ? validationErrors[field] : null;

  const handleChange = useCallback(
    (ev) => {
      const { name, checked } = ev.target;

      setValues((prevValues) => {
        const newValues = checked ? [...prevValues, name] : prevValues.filter((v) => v !== name);

        if (fieldChangeCallback) fieldChangeCallback(field, newValues);
        return newValues;
      });
    },
    [field, fieldChangeCallback],
  );

  return (
    <div className={cn(fieldset.row, fieldset.row_flexible)}>
      {labelId && (
        <label className={fieldset.label}>
          <FormattedMessage id={labelId} />
        </label>
      )}
      <div className={cn(fieldset.control, fieldset.checkbox_control)}>
        <div className={fieldset.group}>
          {options.map((option) => (
            <div key={option} className={fieldset.checkbox}>
              <AuInput
                type={INPUT_TYPE_CHECKBOX}
                name={option}
                caption={option}
                checked={values.includes(option)}
                onChange={handleChange}
                disabled={disabled}
                showError={showErrors}
                error={error}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
